import { AlgoliaUtils } from '@algolia/utils';
class AlgoliaAutocompleteComponent extends HTMLElement {
  constructor(options = {}) {
    super();
    this.options = options;
    this.config = null;
    this.plugins = [];
    this.autocompleteInstance = null;
  }

  connectedCallback() {
    // Define the redirect-button observer helpers first so init()'s
    // onStateChange can drive their connect/disconnect lifecycle.
    this.initRedirectButtonObserver();
    this.init();
  }

  disconnectedCallback() {
    this.disconnectRedirectButtonObserver?.();
    this.redirectButtonObserver = null;
  }

  getPanelContainer() {
    return document.querySelector('#search-panel-container');
  }

  detectLocale() {
    if (window.Shopify?.locale) {
      return window.Shopify.locale;
    }
    return navigator.language || navigator.userLanguage || 'en-US';
  }

  detectCurrency() {
    return (
      window.Shopify?.currency?.active ||
      window.theme?.currency ||
      window.Currency?.currentCurrency ||
      'USD'
    );
  }

  get prefix() {
    const themeRoutes =
      /** @type {any} */ (typeof Theme !== 'undefined' ? Theme : undefined)?.routes ||
      /** @type {any} */ (window.Theme)?.routes ||
      /** @type {any} */ (window.Shopify)?.routes;

    const rawPrefix = themeRoutes?.root_url_prefix || themeRoutes?.root || '/';
    if (typeof rawPrefix !== 'string' || rawPrefix.length === 0) return '/';

    const withLeadingSlash = rawPrefix.startsWith('/') ? rawPrefix : `/${rawPrefix}`;
    return withLeadingSlash.endsWith('/') ? withLeadingSlash : `${withLeadingSlash}/`;
  }

  prefixedPath(path) {
    const rawPath = typeof path === 'string' ? path : '';
    if (!rawPath) return this.prefix;

    // Keep absolute/protocol links untouched.
    if (/^(?:[a-z]+:)?\/\//i.test(rawPath)) return rawPath;

    const normalizedPrefix = this.prefix;

    if (normalizedPrefix === '/') {
      return rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
    }

    const prefixedRootPath = normalizedPrefix;
    if (rawPath.startsWith(prefixedRootPath)) return rawPath;

    const normalizedPath = rawPath.startsWith('/') ? rawPath.slice(1) : rawPath;
    return `${prefixedRootPath}${normalizedPath}`;
  }

  decodeHtmlEntities(text) {
    if (!text) return text;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  getLocalizedText(key, fallback) {
    let result;
    if (window.searchTranslations?.autocomplete?.[key]) {
      result = window.searchTranslations.autocomplete[key];
    } else if (window.autocompleteConfig?.labels?.[key]) {
      result = window.autocompleteConfig.labels[key];
    } else if (window.theme?.strings?.search?.[key]) {
      result = window.theme.strings.search[key];
    } else {
      result = fallback;
    }
    return this.decodeHtmlEntities(result);
  }

  buildQuerySuggestionsIndexName(mainIndex) {
    if (mainIndex.includes('_products')) {
      return mainIndex.replace('_products', '_products_query_suggestions');
    }
    return `${mainIndex}_query_suggestions`;
  }

  getConfig() {
    if (this.config) return this.config;
    const config = {
      appId: window.algoliaConfig?.appId,
      apiKey: window.algoliaConfig?.apiKey,
      indexName: window.algoliaConfig?.indexName || 'shopify_products',
      querySuggestionsIndexName:
        window.autocompleteConfig?.querySuggestionsIndexName ||
        this.buildQuerySuggestionsIndexName(window.algoliaConfig?.indexName || 'shopify_products'),
      categoryAttributes: [window.algoliaConfig?.indexName, 'facets', 'exact_matches', 'tags'],
      locale: window.autocompleteConfig?.locale || this.detectLocale(),
      currency: window.autocompleteConfig?.currency || this.detectCurrency(),
      taxRate: 1 + (window.algoliaConfig?.taxRate || 0),
      hitsPerPage: 5,
      suggestionsPerPage: 5,
      searchUrl: this.prefixedPath(window.autocompleteConfig?.searchUrl || '/search'),
      productUrlPattern: this.prefixedPath(
        window.autocompleteConfig?.productUrlPattern || '/products/{{handle}}'
      ),
      collectionUrlPattern: this.prefixedPath(
        window.autocompleteConfig?.collectionUrlPattern || '/collections/{{handle}}'
      ),
      placeholderImage: window.theme?.assets?.placeholder,
      labels: {
        placeholder: this.getLocalizedText(
          'placeholder',
          window.searchTranslations?.autocomplete?.placeholder || 'Shop durchsuchen...'
        ),
        products: this.getLocalizedText(
          'products',
          window.searchTranslations?.autocomplete?.products || 'Produkte'
        ),
        recentSearches: this.getLocalizedText(
          'recent_searches',
          window.searchTranslations?.autocomplete?.recentSearches || 'Letzte Suchen'
        ),
        popularSearches: this.getLocalizedText(
          'popular_searches',
          window.searchTranslations?.autocomplete?.popularSearches || 'Beliebte Suchen'
        ),
        searchIn: this.getLocalizedText(
          'search_in',
          window.searchTranslations?.autocomplete?.searchIn || 'in'
        ),
        noResultsTitle: this.getLocalizedText(
          'no_results_title',
          window.searchTranslations?.autocomplete?.noResultsTitle || 'KEINE ERGEBNISSE FÜR'
        ),
        noResultsSuggestions: {
          checkSpelling: this.getLocalizedText(
            'check_spelling',
            window.searchTranslations?.autocomplete?.checkSpelling ||
              'Überprüfen Sie Ihre Rechtschreibung'
          ),
          useFewerKeywords: this.getLocalizedText(
            'use_fewer_keywords',
            window.searchTranslations?.autocomplete?.useFewerKeywords ||
              'Weniger Schlüsselwörter verwenden'
          ),
          searchLessSpecific: this.getLocalizedText(
            'search_less_specific',
            window.searchTranslations?.autocomplete?.searchLessSpecific ||
              'Suche nach einem weniger spezifischen Artikel'
          ),
          tryPopularCategories: this.getLocalizedText(
            'try_popular_categories',
            window.searchTranslations?.autocomplete?.tryPopularCategories ||
              'Versuchen Sie zu navigieren, indem Sie eine der beliebten Kategorien verwenden'
          ),
        },
        popularCategories: this.getLocalizedText(
          'popular_categories',
          window.searchTranslations?.autocomplete?.popularCategories || 'BELIEBTE KATEGORIEN'
        ),
        ...window.autocompleteConfig?.labels,
      },
      enableInsights: true,
      enableRedirectPlugin: true,
      enableRecentSearches: true,
      enableQuerySuggestions: true,
    };
    this.config = config;
    return config;
  }
  initRedirectButtonObserver() {
    // Insert the redirect ("View all") button into the provided source header
    const insertRedirectButton = (container) => {
      const input = document.querySelector('.aa-Input');
      if (!input || !container) return;
      // Prevent multiple buttons
      if (container.querySelector('#search-redirect-button')) return;
      // Create the button
      const button = document.createElement('button');
      button.id = 'search-redirect-button';
      button.textContent = window.searchTranslations?.search?.view_all || 'View All';
      // Redirect to search on click — read input value directly
      button.addEventListener('click', () => {
        const { searchUrl } = this.getConfig();
        const query = encodeURIComponent(input.value.trim());
        if (query) {
          window.location.href = `${searchUrl}?q=${query}`;
        }
      });
      // Append to the container
      container.appendChild(button);
    };

    // Scan the given root (the scoped panel container) for source headers and
    // inject the button into each.
    const insertRedirectButtons = (root) => {
      (root ?? document)
        .querySelectorAll('.search-dropdown__right-side .aa-SourceHeader')
        .forEach((container) => {
          insertRedirectButton(container);
        });
    };

    // Observer scoped to the autocomplete panel container (NOT document.body),
    // so Algolia's per-keystroke DOM churn stays contained and no longer
    // re-triggers unrelated document.body observers elsewhere on the page.
    this.redirectButtonObserver = new MutationObserver(() => {
      insertRedirectButtons(this.getPanelContainer());
    });
    this._redirectObserverConnected = false;

    // Connect only while the panel is open (driven by autocomplete's own
    // onStateChange / state.isOpen signal — see init()), never via a
    // mutation-based open/closed heuristic.
    this.connectRedirectButtonObserver = () => {
      if (this._redirectObserverConnected || !this.redirectButtonObserver) return;
      const panel = this.getPanelContainer();
      if (!panel) return;
      this.redirectButtonObserver.observe(panel, { childList: true, subtree: true });
      this._redirectObserverConnected = true;
      // Inject immediately for whatever is already rendered in the panel.
      insertRedirectButtons(panel);
    };

    this.disconnectRedirectButtonObserver = () => {
      if (!this.redirectButtonObserver) return;
      this.redirectButtonObserver.disconnect();
      this._redirectObserverConnected = false;
    };
  }

  formatHighlightedText(text) {
    return text
      .replace(/__aa-highlight__/g, '<strong class="highlight">')
      .replace(/__\/aa-highlight__/g, '</strong>');
  }

  formatUrl(template, data) {
    return template.replace(/{{(\w+)}}/g, (match, key) => data[key] || match);
  }

  handleize(str) {
    const locale = this.getConfig().locale.toLowerCase();
    let processedStr = str.toLowerCase();
    if (locale.startsWith('de')) {
      processedStr = processedStr.replace(/[üöä]/g, (match) => {
        const umlautMap = { ü: 'ue', ö: 'oe', ä: 'ae' };
        return umlautMap[match];
      });
    }
    if (locale.startsWith('fr')) {
      processedStr = processedStr
        .replace(/[àâä]/g, 'a')
        .replace(/[éèêë]/g, 'e')
        .replace(/[îï]/g, 'i')
        .replace(/[ôö]/g, 'o')
        .replace(/[ùûü]/g, 'u')
        .replace(/ç/g, 'c');
    }
    return processedStr.replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }

  createSearchClient(config) {
    const algoliaClient = window.algoliasearch.algoliasearch(
      config.appId || '{{ settings.algolia_app_id }}',
      config.apiKey || '{{ settings.algolia_api_key }}'
    );
    return {
      ...algoliaClient,
      search(requests) {
        if (!requests || requests.length === 0) {
          return algoliaClient.search(requests);
        }
        const modifiedRequests = requests.map((request) => {
          const query = request.params?.query || '';
          if (query.trim().length === 0) {
            return {
              ...request,
              params: {
                ...request.params,
                analytics: false,
              },
            };
          }
          return {
            ...request,
            params: {
              ...request.params,
              analyticsTags: ['typed-search', 'autocomplete'],
            },
          };
        });
        return algoliaClient.search(modifiedRequests);
      },
    };
  }

  createNoResultsTemplate(query, html, config) {
    return html`
      <div class="search-dropdown__no-results">
        <div class="search-dropdown__no-results-content">
          <h2 class="search-dropdown__no-results-title h6">
            ${config.labels.noResultsTitle} "${query}"
          </h2>
          <ul class="search-dropdown__suggestions">
            <li>${config.labels.noResultsSuggestions.checkSpelling}</li>
            <li>${config.labels.noResultsSuggestions.useFewerKeywords}</li>
            <li>${config.labels.noResultsSuggestions.searchLessSpecific}</li>
            <li>${config.labels.noResultsSuggestions.tryPopularCategories}</li>
          </ul>
        </div>
        <div class="search-dropdown__categories">
          <h3 class="search-dropdown__categories-title h6">${config.labels.popularCategories}</h3>
          <div class="search-dropdown__categories-grid">
            ${window.theme?.assets?.autocomplete_collection_image_01
              ? html`
                  <a
                    href="${this.prefixedPath(
                      window.theme?.assets?.autocomplete_collection_url_01 ||
                        '/collections/collection-1'
                    )}"
                    class="search-dropdown__category-card"
                  >
                    <img
                      src="${window.theme?.assets?.autocomplete_collection_image_01}"
                      alt="${window.theme?.assets?.autocomplete_collection_text_01 || ''}"
                    />
                    <span>${window.theme?.assets?.autocomplete_collection_text_01 || ''}</span>
                  </a>
                `
              : ''}
            ${window.theme?.assets?.autocomplete_collection_image_02
              ? html`
                  <a
                    href="${this.prefixedPath(
                      window.theme?.assets?.autocomplete_collection_url_02 ||
                        '/collections/collection-2'
                    )}"
                    class="search-dropdown__category-card"
                  >
                    <img
                      src="${window.theme?.assets?.autocomplete_collection_image_02}"
                      alt="${window.theme?.assets?.autocomplete_collection_text_02 || ''}"
                    />
                    <span>${window.theme?.assets?.autocomplete_collection_text_02 || ''}</span>
                  </a>
                `
              : ''}
            ${window.theme?.assets?.autocomplete_collection_image_03
              ? html`
                  <a
                    href="${this.prefixedPath(
                      window.theme?.assets?.autocomplete_collection_url_03 ||
                        '/collections/collection-3'
                    )}"
                    class="search-dropdown__category-card"
                  >
                    <img
                      src="${window.theme?.assets?.autocomplete_collection_image_03}"
                      alt="${window.theme?.assets?.autocomplete_collection_text_03 || ''}"
                    />
                    <span>${window.theme?.assets?.autocomplete_collection_text_03 || ''}</span>
                  </a>
                `
              : ''}
            ${window.theme?.assets?.autocomplete_collection_image_04
              ? html`
                  <a
                    href="${this.prefixedPath(
                      window.theme?.assets?.autocomplete_collection_url_04 ||
                        '/collections/collection-4'
                    )}"
                    class="search-dropdown__category-card"
                  >
                    <img
                      src="${window.theme?.assets?.autocomplete_collection_image_04}"
                      alt="${window.theme?.assets?.autocomplete_collection_text_04 || ''}"
                    />
                    <span>${window.theme?.assets?.autocomplete_collection_text_04 || ''}</span>
                  </a>
                `
              : ''}
            ${window.theme?.assets?.autocomplete_collection_image_05
              ? html`
                  <a
                    href="${this.prefixedPath(
                      window.theme?.assets?.autocomplete_collection_url_05 ||
                        '/collections/collection-5'
                    )}"
                    class="search-dropdown__category-card"
                  >
                    <img
                      src="${window.theme?.assets?.autocomplete_collection_image_05}"
                      alt="${window.theme?.assets?.autocomplete_collection_text_05 || ''}"
                    />
                    <span>${window.theme?.assets?.autocomplete_collection_text_05 || ''}</span>
                  </a>
                `
              : ''}
            ${window.theme?.assets?.autocomplete_collection_image_06
              ? html`
                  <a
                    href="${this.prefixedPath(
                      window.theme?.assets?.autocomplete_collection_url_06 ||
                        '/collections/collection-6'
                    )}"
                    class="search-dropdown__category-card"
                  >
                    <img
                      src="${window.theme?.assets?.autocomplete_collection_image_06}"
                      alt="${window.theme?.assets?.autocomplete_collection_text_06 || ''}"
                    />
                    <span>${window.theme?.assets?.autocomplete_collection_text_06 || ''}</span>
                  </a>
                `
              : ''}
          </div>
        </div>
      </div>
    `;
  }

  createProductTemplate({ item, html }, config) {
    const highlightedTitle = item._highlightResult?.title?.value || item.title;
    const formattedTitle = this.formatHighlightedText(highlightedTitle);
    const imageUrl = item.image || config.placeholderImage;
    const imageSrcset = item.image_srcset || '';
    const { price, compare_at_price: compareAtPrice } = item;
    const productUrl = this.formatUrl(config.productUrlPattern, { handle: item.handle });
    const productUrlWithQuery = `${productUrl}?queryID=${item.__autocomplete_queryID}`;
    return html`
      <a
        href="${productUrlWithQuery}"
        product-id="${item.objectID}"
        data-product-handle="${item.handle}"
        class="apc order-1"
      >
        <div class="apc__image">
          <div class="image contain">
            <img
              src="${imageUrl}"
              alt="${item.title}"
              srcset="${imageSrcset}"
              width="500"
              height="218"
              class="image__img contain"
              onerror="this.src='${config.placeholderImage}'"
            />
          </div>
        </div>
        <div class="apc_info">
          <div class="apc__category h6">${item.vendor}</div>
          <span class="apc__title">${html([formattedTitle])}</span>
          <div class="apc__price-wrapper">
            ${html`
              <span class="apc__price">
                <span class="price">
                  <span class="price__amount--b2b"
                    >${AlgoliaUtils.formatPriceWithCurrency(price)}</span
                  >
                  <span class="price__amount--b2c"
                    >${AlgoliaUtils.formatPriceWithCurrency(price * AlgoliaUtils.TAX_RATE)}</span
                  >
                </span>
                ${compareAtPrice && compareAtPrice > price
                  ? html`
                      <span class="compare-at-price">
                        <span class="price__amount--b2b"
                          >${AlgoliaUtils.formatPriceWithCurrency(compareAtPrice)}</span
                        >
                        <span class="price__amount--b2c"
                          >${AlgoliaUtils.formatPriceWithCurrency(
                            compareAtPrice * AlgoliaUtils.TAX_RATE
                          )}</span
                        >
                      </span>
                    `
                  : ''}
              </span>
            `}
          </div>
        </div>
      </a>
    `;
  }

  async init() {
    // UZ-8543: wait without triggering the download; algolia-scripts loads the libraries in idle
    // time or on the first search bar interaction. Until then the static placeholder form is usable.
    await window.algoliaLibsLoaded;
    if (!window.algoliasearch || !window['@algolia/autocomplete-js']) {
      console.error('Algolia libraries not found');
      return;
    }
    const config = this.getConfig();
    const trackDataLayerEvent = (eventName, data = {}) => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...data });
    };
    const bindSearchTracking = () => {
      const input =
        this.querySelector('#search-container input') ||
        document.querySelector('#search-container input');
      if (input && !input.dataset.aaFocusTracked) {
        input.addEventListener('focus', () =>
          trackDataLayerEvent('search_aa_focus_dev', {
            search_aa_type: 'aa-search',
            search_aa_id: input.id || null,
            search_aa_placeholder: input.placeholder || null,
          })
        );
        input.dataset.aaFocusTracked = 'true';
      }
      if (!this.dataset.aaClickListener) {
        this.addEventListener('click', (event) => {
          const target = event.target;
          if (!(target instanceof Element)) return;
          const clickedItem = target.closest(
            'a[product-id], .auto-suggestions, .search-dropdown__category-card, #search-redirect-button'
          );
          if (!clickedItem) return;
          let searchType = 'aa-history';
          if (clickedItem.matches('a[product-id]')) {
            searchType = 'aa-product';
          } else if (clickedItem.matches('.auto-suggestions')) {
            searchType = 'aa-suggestion';
          }
          const clickedText =
            clickedItem.getAttribute('aria-label') || clickedItem.textContent || '';
          trackDataLayerEvent('search_aa_click_dev', {
            search_aa_type: searchType,
            search_aa_text: clickedText.trim() || input?.value?.trim() || '',
          });
        });
        this.dataset.aaClickListener = 'true';
      }
    };
    const { autocomplete, getAlgoliaResults } = window['@algolia/autocomplete-js'];
    const { createQuerySuggestionsPlugin } =
      window['@algolia/autocomplete-plugin-query-suggestions'];
    const { createLocalStorageRecentSearchesPlugin } =
      window['@algolia/autocomplete-plugin-recent-searches'];
    const { createRedirectUrlPlugin } = window['@algolia/autocomplete-plugin-redirect-url'];
    const searchClient = this.createSearchClient(config);
    this.plugins = [];

    // Recent searches plugin
    const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
      key: 'navbar',
      transformSource: ({ source }) => ({
        ...source,
        onSelect: (params) => {
          const redirectData = params.state.context?.redirectUrlPlugin?.data;
          if (redirectData && redirectData.length > 0) {
            const redirectUrl = redirectData[0].urls[0];
            if (redirectUrl) {
              window.location.href = this.prefixedPath(redirectUrl);
              return;
            }
          }
          const searchTerm = params.item.label;
          window.location.href = `${config.searchUrl}?q=${encodeURIComponent(searchTerm)}`;
        },
      }),
    });
    this.plugins.push(recentSearchesPlugin);

    // Query suggestions plugin
    const querySuggestionsPlugin = createQuerySuggestionsPlugin({
      searchClient: searchClient,
      indexName: config.querySuggestionsIndexName,
      getSearchParams: () => ({ hitsPerPage: config.suggestionsPerPage }),
      transformSource: ({ source }) => ({
        ...source,
        templates: {
          ...source.templates,
          item: ({ item, html }) => {
            const highlightedQuery = item._highlightResult?.query?.value || item.query;
            const formattedQuery = this.formatHighlightedText(highlightedQuery);
            const category = item.__autocomplete_qsCategory;
            const getUrl = () => {
              if (category) {
                const categoryHandle = this.handleize(category);
                return this.formatUrl(config.collectionUrlPattern, { handle: categoryHandle });
              }
              return `${config.searchUrl}?q=${encodeURIComponent(item.query)}`;
            };
            return html`
              <a href="${getUrl()}" class="auto-suggestions">
                <div class="auto-suggestions__svg-holder">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M11.6057 6.55286C11.6057 9.34348 9.34348 11.6057 6.55287 11.6057C3.76225 11.6057 1.5 9.34348 1.5 6.55286C1.5 3.76224 3.76224 1.5 6.55286 1.5C9.34348 1.5 11.6057 3.76224 11.6057 6.55286ZM10.6258 11.6866C9.50743 12.575 8.09213 13.1057 6.55287 13.1057C2.93382 13.1057 1.77851e-06 10.1719 1.14574e-06 6.55286C5.12965e-07 2.93382 2.93382 -5.12965e-07 6.55286 -1.14574e-06C10.1719 -1.77851e-06 13.1057 2.93381 13.1057 6.55286C13.1057 8.09219 12.575 9.50755 11.6864 10.6259L15.9452 14.8846L14.8845 15.9453L10.6258 11.6866Z"
                      fill="#838487"
                    ></path>
                  </svg>
                </div>
                <span class="auto-suggestions__text">${html([formattedQuery])}</span>
                ${category
                  ? html`<span
                      class="text text--body-font text--italic"
                      style="--text-color: var(--text-color--secondary)"
                    >
                      - ${config.labels.searchIn} ${category}</span
                    >`
                  : ''}
              </a>
            `;
          },
        },
      }),
      categoryAttribute: config.categoryAttributes,
      itemsWithCategories: 2,
      categoriesPerItem: 1,
    });
    this.plugins.push(querySuggestionsPlugin);

    // Redirect URL plugin
    const redirectUrlPlugin = createRedirectUrlPlugin();
    this.plugins.push(redirectUrlPlugin);

    // Product template
    const createProductTemplate = (args) => this.createProductTemplate(args, config);

    // No results template
    const createNoResultsTemplate = (query, html) =>
      this.createNoResultsTemplate(query, html, config);
    const isCollectionPage =
      window.location.pathname.includes('/collections') ||
      window.location.pathname.includes('/search');
    // UZ-8543: hand over from the static placeholder form (text typed and focus before load)
    const placeholder = this.querySelector('[data-algolia-search-placeholder]');
    const placeholderInput = placeholder?.querySelector('input');
    const pendingQuery = placeholderInput?.value ?? '';
    const hadFocus = !!placeholderInput && document.activeElement === placeholderInput;
    placeholder?.remove();
    // Initialize autocomplete
    const autocompleteInstance = autocomplete({
      container: '#search-container',
      panelContainer: '#search-panel-container',
      panelPlacement: 'full-width',
      detachedMediaQuery: 'none',
      placeholder: config.labels.placeholder,
      openOnFocus: true,
      insights: config.enableInsights,
      plugins: this.plugins,
      onSubmit: ({ state }) => {
        const searchTerm = state.query.trim();
        if (searchTerm) {
          window.location.href = `${config.searchUrl}?q=${encodeURIComponent(searchTerm)}`;
        }
        if (window.ProductSearch?.setInstantSearchUiState) {
          window.ProductSearch.setInstantSearchUiState({ query: state.query });
        }
      },
      onReset: () => {
        if (window.ProductSearch?.setInstantSearchUiState) {
          window.ProductSearch.setInstantSearchUiState({ query: '' });
        }
      },
      onStateChange: ({ prevState, state }) => {
        bindSearchTracking();
        // Auf Collection-Seiten KEIN Live-Sync
        if (
          !isCollectionPage &&
          prevState.query !== state.query &&
          window.ProductSearch?.setInstantSearchUiState
        ) {
          window.ProductSearch.setInstantSearchUiState({ query: state.query });
        }
        // Drive the (panel-scoped) redirect-button observer off the library's
        // own open/closed signal: observe while open, disconnect once closed.
        if (state.isOpen) {
          this.connectRedirectButtonObserver?.();
        } else {
          this.disconnectRedirectButtonObserver?.();
        }
        const overlay = this.querySelector('.aa-DetachedOverlay');
        if (state.isOpen) {
          if (!overlay) {
            const newOverlay = document.createElement('div');
            newOverlay.className = 'aa-DetachedOverlay';
            newOverlay.addEventListener('click', () => {
              const input = this.querySelector('#search-container input');
              if (input) {
                input.value = '';
                input.blur();
              }
            });
            this.appendChild(newOverlay);
          }
        } else if (overlay) {
          overlay.remove();
        }
      },
      getSources: ({ query }) => [
        {
          sourceId: 'products',
          getItems: () =>
            getAlgoliaResults({
              searchClient: searchClient,
              queries: [
                {
                  indexName: config.indexName,
                  query,
                  params: {
                    hitsPerPage: config.hitsPerPage,
                  },
                },
              ],
              // PP-1011/PP-988: products with the template suffix `warranty` (warranty
              // add-ons, required add-ons) never get a card, in the dropdown as well
              transformResponse: ({ hits }) =>
                hits.map((group) =>
                  group.filter((hit) => hit && hit.template_suffix !== 'warranty')
                ),
            }),
          getItemInputValue: ({ item }) => item.title,
          templates: {
            item: createProductTemplate,
            header: ({ html }) =>
              html`<span class="search-dropdown__heading h5">${config.labels.products}</span>`,
            noResults: ({ html }) => createNoResultsTemplate(query, html),
          },
        },
      ],
      render: (context, root) => {
        const { elements, render, html, state } = context;
        const {
          querySuggestionsPlugin: querySuggestionsElement,
          products,
          recentSearchesPlugin: recentSearchesElement,
        } = elements;
        if (state.query) {
          const hasProducts = state.collections.some(
            (collection) => collection.source.sourceId === 'products' && collection.items.length > 0
          );
          const hasSuggestions = state.collections.some(
            (collection) =>
              collection.source.sourceId === 'querySuggestionsPlugin' && collection.items.length > 0
          );
          if (!hasProducts && !hasSuggestions) {
            render(createNoResultsTemplate(state.query, html), root);
            return;
          }
          render(
            html`
              <div class="search-dropdown__left-side">${querySuggestionsElement}</div>
              <div class="search-dropdown__right-side">${products}</div>
            `,
            root
          );
          return;
        }
        if (recentSearchesElement) {
          render(
            html`
              <div class="search-dropdown__left-side">
                <span class="search-dropdown__heading h5">${config.labels.recentSearches}</span>
                ${recentSearchesElement}
              </div>
              <div class="search-dropdown__right-side">
                <span class="search-dropdown__heading h5">${config.labels.popularSearches}</span>
                ${querySuggestionsElement}
              </div>
            `,
            root
          );
          return;
        }
        render(
          html` <div class="search-dropdown__left-side">${querySuggestionsElement}</div> `,
          root
        );
      },
    });
    bindSearchTracking();
    if (pendingQuery) autocompleteInstance.setQuery(pendingQuery);
    if (hadFocus) {
      this.querySelector('#search-container .aa-Input')?.focus();
      autocompleteInstance.setIsOpen(true);
      autocompleteInstance.refresh();
    }
  }
}

customElements.define('algolia-searchbar', AlgoliaAutocompleteComponent);
